import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { AssetReadExternal, SearchTaskFilter, TeamReadExternal, UserReadExternal } from '@askporter/client-grieg-lyric';
import { fullName } from '@askporter/utils';
import { API } from '../api';
type EntityType = 'user' | 'team' | 'asset';
interface EntityRef {
  type: EntityType;
  uid: string;
}
/**
 * Deliberately its own key namespace, not the canonical per-entity ones (e.g. queryKeys.assets.detail) other
 * pages use to cache the full entity. Sharing those looks appealing (free cache hits, no duplicate fetch),
 * but this query's data is a resolved *string*, not the full entity - if a filtered uid happens to match
 * something already cached full-shape under that same key elsewhere (the asset currently being viewed, a
 * user's own profile, ...), React Query serves that cached value here without ever calling this fetcher, and
 * the raw entity ends up where only a name string is expected.
 */
const queryKeyForRef = ({ type, uid }: EntityRef): readonly unknown[] => ['task-filter-chip-entity-name', type, uid];
/**
 * Fetches a single entity by uid and resolves its display name using the established FE conventions: users
 * -> full name, teams -> `name`, assets -> `title`.
 */
const fetchEntityName = async ({ type, uid }: EntityRef): Promise<string> => {
  switch (type) {
    case 'user': {
      const user: UserReadExternal = await API().get({ path: `users/${uid}` });
      return fullName(user?.givenName, user?.familyName);
    }
    case 'team': {
      const team: TeamReadExternal = await API().get({ path: `teams/${uid}` });
      return team?.name ?? '';
    }
    case 'asset': {
      const asset: AssetReadExternal = await API().get({ path: `assets/${uid}` });
      return asset?.title ?? '';
    }
    default:
      return '';
  }
};
export interface EntityFilterNameLookup {
  /** the resolved display name, once available */
  name?: string;
  /** true while the name is still being fetched - callers can show a skeleton rather than a raw uid */
  isLoading: boolean;
}
/**
 * Resolves display names for every uid currently selected across the entity-based task filters (linked/
 * assigned/escalated-to users, assigned/escalated-to teams, linked asset), so the active-filter chips can
 * show a real name instead of a raw uid. One cached GET per uid, deduped across categories and re-renders.
 * A uid that fails to resolve (deleted, no permission) settles with `isLoading: false` and no `name`, so
 * callers can fall back to the raw uid rather than showing a skeleton forever.
 *
 * @param filters - the currently-applied SearchTaskFilter
 */
export const useEntityFilterNames = (filters: SearchTaskFilter): Record<string, EntityFilterNameLookup> => {
  const refs = useMemo((): EntityRef[] => {
    const userUids = new Set([
      ...(filters.linkedUser ?? []),
      ...(filters.assignedUser ?? []),
      ...(filters.escalatedToUser ?? []),
    ]);
    const teamUids = new Set([...(filters.assignedTeam ?? []), ...(filters.escalatedToTeam ?? [])]);
    const assetUids = new Set(filters.linkedAsset ?? []);
    return [
      ...Array.from(userUids, (uid): EntityRef => ({ type: 'user', uid })),
      ...Array.from(teamUids, (uid): EntityRef => ({ type: 'team', uid })),
      ...Array.from(assetUids, (uid): EntityRef => ({ type: 'asset', uid })),
    ];
  }, [
    filters.linkedUser,
    filters.assignedUser,
    filters.escalatedToUser,
    filters.assignedTeam,
    filters.escalatedToTeam,
    filters.linkedAsset,
  ]);
  const results = useQueries({
    queries: refs.map((ref) => ({
      queryKey: queryKeyForRef(ref),
      queryFn: () => fetchEntityName(ref),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });
  // small, cheap to rebuild every render - no need to memoize against useQueries' own result identity
  return refs.reduce<Record<string, EntityFilterNameLookup>>((acc, ref, index) => {
    const result = results[index];
    acc[ref.uid] = { name: result?.data || undefined, isLoading: result?.isPending ?? false };
    return acc;
  }, {});
};
